import {
  CreateTaxRateDto,
  LocationTaxRateEntity,
  SetTaxRatesDto,
  TaxRateSuggestion,
  UpdateTaxRateDto,
} from '../types/taxRate.types';
import { MenuV2Response } from '../types/menu.types';
import { ResponseType } from '../types/shared.types';
import { api } from './api';

/**
 * Tax rates. Admin app only (`admin` and `support` roles).
 *
 * The four location routes own the rates themselves; the two menu routes below
 * assign them. They live together here because a screen that touches one
 * almost always touches the other - the picker on an item reads the location's
 * list and writes the item's selection.
 *
 * On a `pos` location every one of these answers 400: the POS is the source of
 * truth there. Read the mode off the location (`GET /v2/locations/:id`) and
 * render the rates read-only rather than relying on the error.
 */

export const getLocationTaxRates = (locationId: string | number) =>
  api.get<ResponseType<LocationTaxRateEntity[]>>(
    `/v2/locations/${locationId}/tax-rates`
  );

export const createLocationTaxRate = (
  locationId: string | number,
  data: CreateTaxRateDto
) =>
  api.post<ResponseType<LocationTaxRateEntity>>(
    `/v2/locations/${locationId}/tax-rates`,
    data
  );

export const updateLocationTaxRate = (
  locationId: string | number,
  taxRateId: string | number,
  data: UpdateTaxRateDto
) =>
  api.put<ResponseType<LocationTaxRateEntity>>(
    `/v2/locations/${locationId}/tax-rates/${taxRateId}`,
    data
  );

/**
 * The rate the AI reads off the location's address. Writes nothing - the
 * answer goes in the form and a person saves it.
 *
 * Slow on purpose: the model searches the web, because a municipal meals tax
 * is in no sales-tax database. Budget 10-20 seconds per call.
 */
export const suggestLocationTaxRate = (locationId: string | number) =>
  api.get<ResponseType<TaxRateSuggestion>>(
    `/v2/locations/${locationId}/tax-rates/suggest`
  );

/** Soft delete: the rate leaves the list, assignments that used it stay. */
export const deleteLocationTaxRate = (
  locationId: string | number,
  taxRateId: string | number
) =>
  api.delete<ResponseType<null>>(
    `/v2/locations/${locationId}/tax-rates/${taxRateId}`
  );

/**
 * Both assignment routes answer with the WHOLE menu, like every other menu
 * edit - write it straight over the open menu, no separate refetch. No
 * `menuId` is sent: the menu is derived from the item or option itself.
 */

export const updateMenuItemTaxRates = (
  locationId: string | number,
  brandId: string | number,
  itemId: string | number,
  data: SetTaxRatesDto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/item/${brandId}/update/${itemId}/tax-rates`,
    data
  );

export const updateMenuModifierTaxRates = (
  locationId: string | number,
  brandId: string | number,
  modifierId: string | number,
  data: SetTaxRatesDto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/modifier/${brandId}/update/${modifierId}/tax-rates`,
    data
  );
